<template>
    <bottom-sheet v-model="shows" v-bind="{ title }" fullscreen persistent width="1080" maxWidth="100%">
        <template #activator="bind">
            <slot name="activator" v-bind="bind" />
        </template>
        <v-layout column justify-center class="bottom-sheet__contents h-100">
            <v-sheet class="scroll-primary pr-10" style="flex: 0 1 auto; overflow-y: auto; overflow-x: hidden">
                <h5 class="page-text--lg mb-8 mb-lg-16 line-height-15">
                    <span> 기본 설정 </span>
                </h5>
                <form-base v-model="form" @input="emit" />

                <h5 class="page-text--lg mt-24 mt-lg-48 mb-8 mb-lg-16 line-height-15">
                    <span> 기간 설정 </span>
                </h5>
                <form-time v-model="form" @input="emit" />

                <h5 class="page-text--lg mt-24 mt-lg-48 mb-8 mb-lg-16 line-height-15">
                    <span> 프로그램 상세 </span>
                </h5>
                <v-divider class="primary border-2 mb-10 mb-md-16" />
                <naver-smarteditor v-model="form.content" @input="emit" />
            </v-sheet>
            <div class="v-btn-group">
                <form-prev :program="value">
                    <template #activator="{ attrs, on }">
                        <v-btn outlined color="primary" class="min-w-140px min-w-lg-160px" v-bind="attrs" v-on="on">
                            <span> 미리보기 </span>
                        </v-btn>
                    </template>
                </form-prev>
                <v-btn outlined color="grey-d" class="min-w-140px min-w-lg-160px" @click="shows = false">
                    <span class="grey-6--text"> 취소 </span>
                </v-btn>
                <v-btn color="primary" class="min-w-140px min-w-lg-160px" @click="save">
                    <span> {{ execText }} </span>
                </v-btn>
            </div>
        </v-layout>
    </bottom-sheet>
</template>

<script>
import api from "@/api";
import { initExpoProgram } from "@/assets/variables";

import BottomSheet from "@/components/dumb/bottom-sheet.vue";
import FormBase from "./form/form-base.vue";
import FormTime from "./form/form-time.vue";
import NaverSmarteditor from "@/components/plugins/naver/naver-smarteditor.vue";
import FormPrev from "./form/form-prev.vue";

export default {
    components: {
        BottomSheet,
        FormBase,
        FormTime,
        NaverSmarteditor,
        FormPrev,
    },
    props: {
        value: { type: Object, default: initExpoProgram },
    },
    data: () => ({
        form: initExpoProgram(),

        tab: 0,
        shows: false,
        loading: false,
    }),
    computed: {
        isCreate() {
            return !this.value?._id;
        },
        title() {
            let title = "프로그램";

            if (this.isCreate) title += " 생성";
            else title += " 수정";

            return title;
        },
        execText() {
            if (this.isCreate) return "신청하기";
            else return "수정하기";
        },
    },
    watch: {
        shows() {
            if (this.shows) this.init();
            else if (this.form?._id) this.$emit("input", this.form);
        },
    },
    methods: {
        async init() {
            this.tab = 0;
            this.loading = false;
            if (this.isCreate) {
                this.form = initExpoProgram();
            } else {
                this.loading = true;
                try {
                    let { program } = await api.v1.company.expo.host.programs.get({ _id: this.value._id });
                    this.form = initExpoProgram(program);
                } finally {
                    this.loading = false;
                }
            }
        },

        validate() {
            try {
                if (!this.form.subject) {
                    throw new Error("제목을 입력해주세요");
                }
                if (!this.form.summary) {
                    throw new Error("한줄소개를 입력해주세요");
                }
                if (!this.form.host?.name) {
                    throw new Error("주관기업을 입력해주세요");
                }
                if (!this.form.host?.phone) {
                    throw new Error("연락처를 입력해주세요");
                }
                if (!this.form.host?.email) {
                    throw new Error("이메일을 입력해주세요");
                }
                if ((this.form.tags?.length || 0) < 1) {
                    throw new Error("검색태그를 입력해주세요");
                }

                if (!this.form.date?.programDisplay?.startsAt) {
                    throw new Error("진행기간(시작)을 입력해주세요");
                }
                if (!this.form.date?.programDisplay?.endsAt) {
                    throw new Error("진행기간(종료)을 입력해주세요");
                }

                if (!this.form.date?.employerSubmit?.startsAt) {
                    throw new Error("기업회원 접수기간(시작)을 입력해주세요");
                }
                if (!this.form.date?.employerSubmit?.endsAt) {
                    throw new Error("기업회원 접수기간(종료)을 입력해주세요");
                }

                if (!this.form.date?.employeeSubmit?.startsAt) {
                    throw new Error("개인회원 접수기간(시작)을 입력해주세요");
                }
                if (!this.form.date?.employeeSubmit?.endsAt) {
                    throw new Error("개인회원 접수기간(종료)을 입력해주세요");
                }

                if (!this.form.date?.matchingSubmit?.startsAt) {
                    throw new Error("매칭기간(시작)을 입력해주세요");
                }
                if (!this.form.date?.matchingSubmit?.endsAt) {
                    throw new Error("매칭기간(종료)을 입력해주세요");
                }

                if (!this.form.date?.interviewSpans?.startsAt) {
                    throw new Error("면접기간(시작)을 입력해주세요");
                }
                if (!this.form.date?.interviewSpans?.endsAt) {
                    throw new Error("면접기간(종료)을 입력해주세요");
                }

                if (!this.form.content) {
                    throw new Error("프로그램 상세를 입력해주세요");
                }

                return true;
            } catch (error) {
                alert(error.message);
                return false;
            }
        },

        async save() {
            if (!this.validate()) return;

            if (this.loading) return;
            else this.loading = true;

            try {
                let { _image, image, _thumb, thumb, ...form } = this.form;

                const { post, put } = api.v1.company.expo.host.programs;
                let { program } = await (this.isCreate ? post : put)(form);

                if (image instanceof File) {
                    image = (await api.v1.me.files.post({ index: `${program?._id}-expo-files-image` }, image))?.file;
                }
                _image = image?._id || null;

                if (thumb instanceof File) {
                    thumb = (await api.v1.me.files.post({ index: `${program?._id}-expo-files-thumb` }, thumb))?.file;
                }
                _thumb = thumb?._id || null;

                program = (await put({ _id: program._id, _thumb, _image }))?.program;

                this.form = initExpoProgram(program);
                this.$emit("input", this.form);
                this.shows = false;
            } finally {
                if (this.loading) this.loading = false;
            }
        },

        async emit() {
            this.form = initExpoProgram(this.form);
        },
    },
};
</script>

<style lang="scss" scoped>
::v-deep {
    .text-right input {
        text-align: right;
    }
    .v-input--switch label {
        margin-bottom: 4px;
    }
    .v-textarea textarea {
        margin: 0 !important;
        padding-top: 8px;
        line-height: 1.875em;
    }
}
</style>
